import { useMyEntitlements } from "@/hooks/useMyEntitlements";
import { usePermissions } from "@/hooks/usePermissions";
import { usePlans } from "@/hooks/usePlans";

interface PlanBadgeProps {
  /** Renders this text in place of the entitlement-derived label and bypasses all self-hiding
   * checks below (GFG staff, entitlements still loading). For callers with no plan/tier of
   * their own — e.g. the GFG admin pill on /account — that still want the same pill chrome. */
  overrideLabel?: string;
}

/**
 * Top-bar plan pill. Self-contained and self-hiding by default — no props needed for the
 * ordinary client-role case.
 *
 * Reads the entitlement matrix rather than GET /tier, which fixes what it showed. /tier is gated
 * on TIER view, so Manager and View Only got a 403 that useTier turned into "starter" — those
 * roles saw a Starter pill on a Pro organization. Entitlements are readable by any member of the
 * organization, because reading your own plan's limits is not reading its billing.
 *
 * The badge label comes from the catalogue, so it says what the plan cards say.
 */
export default function PlanBadge({ overrideLabel }: PlanBadgeProps = {}) {
  const { ent } = useMyEntitlements();
  const { isGfg, isLoaded } = usePermissions();
  const { byKey } = usePlans();

  const label = overrideLabel ?? (ent ? byKey(ent.plan.key)?.display.badgeLabel : undefined);

  // isGfg is false while caps are in flight, so !isLoaded is what stops the pill from
  // flashing at GFG staff before both queries agree. overrideLabel skips all of that: the
  // caller already knows who it's rendering for.
  if (!overrideLabel && (!isLoaded || isGfg || !label)) return null;

  return (
    <span
      data-testid="plan-badge"
      // `-top-px` optically centers the pill against the top bar's larger serif title.
      className="relative -top-px inline-block shrink-0 whitespace-nowrap rounded-full bg-good-night-4 px-[10px] py-[4px] font-lato text-[11px] font-bold uppercase leading-[1.4] tracking-[.05em] text-good-night"
    >
      {label}
    </span>
  );
}
